import React from 'react';
import { ButtonLinkPill } from '../ButtonLink';
import Pill from '../Pill';
import RichTextRender from '../RichTextRender';
import styles from './NewsDetail.module.scss';
import { CONTENTFUL_PAGE_CONTENT_TYPES } from '@src/constants/contentful';
import { Column, Container, Row } from '@src/elements/Grid';
import Heading from '@src/elements/Heading';
import {
  PageNationalNewsFieldsFragment,
  PageCommunityNewsFieldsFragment
} from '@src/graphql/gql-types';
import { COLOR_BLUE, COLOR_SLATE } from '@src/types/colors';
import { WordDictionary } from '@src/types/dictionary';
import { RICHTEXT_RENDER_MODE } from '@src/types/richText';
import { SVG_PDF } from '@src/types/svg';
import { isLocalUrl } from '@src/utils/url';

interface NewsDetail
  extends Omit<
    PageNationalNewsFieldsFragment | PageCommunityNewsFieldsFragment,
    'sys' | '__typename'
  > {
  replacementDictionary?: WordDictionary;
}

const NewsDetail: React.FC<NewsDetail> = ({
  readingTime,
  introText,
  bodyContent,
  downloadablePdFsCollection,
  relatedLinksCollection,
  replacementDictionary
}) => (
  <div className={styles.newsDetail}>
    <Container>
      <Row>
        <Column xl={8}>
          {(readingTime || readingTime === 0) && (
            <div className={styles.readingTime}>
              <Pill color={COLOR_SLATE} text={`${readingTime} min read`} />
            </div>
          )}

          <Heading priority={4} fontStyle="h4">
            {introText}
          </Heading>

          {bodyContent && (
            <RichTextRender
              bodyContent={bodyContent}
              pageType={CONTENTFUL_PAGE_CONTENT_TYPES.NATIONAL_NEWS}
              renderMode={RICHTEXT_RENDER_MODE.ALLOW_BLOCK_EMBEDS}
              replacementDictionary={replacementDictionary}
            />
          )}
        </Column>

        <Column xl={4}>
          {!!downloadablePdFsCollection?.items.length && (
            <div className={styles.details}>
              <Heading priority={4} fontStyle="h4">
                Downloads
              </Heading>

              {downloadablePdFsCollection?.items.map((pdf, index) => (
                <ButtonLinkPill
                  key={index}
                  color={COLOR_BLUE}
                  icon={SVG_PDF}
                  iconPosition="left"
                  href={pdf?.cloudinarySource[0]?.original_secure_url}
                  openNewWindow
                >
                  {pdf?.caption}
                </ButtonLinkPill>
              ))}
            </div>
          )}

          {!!relatedLinksCollection?.items.length && (
            <div className={styles.details}>
              <Heading priority={4} fontStyle={'h4'}>
                Related links
              </Heading>

              {relatedLinksCollection?.items?.map((link, index) => {
                if (link?.__typename === 'UrlLinks' && link.urlLink) {
                  const openInNewWindow = !isLocalUrl(link.urlLink);
                  return (
                    <ButtonLinkPill
                      key={index}
                      color={COLOR_BLUE}
                      openNewWindow={openInNewWindow}
                      href={link.urlLink}
                    >
                      {link.label}
                    </ButtonLinkPill>
                  );
                } else if (link?.__typename !== 'UrlLinks' && link?.slug) {
                  return (
                    <ButtonLinkPill
                      key={index}
                      color={COLOR_BLUE}
                      href={link.slug}
                    >
                      {link.title}
                    </ButtonLinkPill>
                  );
                }
              })}
            </div>
          )}
        </Column>
      </Row>
    </Container>
  </div>
);

export default NewsDetail;
